import {
  DepositPayable,
  FormCondition,
  FormConditionCondition,
} from '@urbanx/agx.campaigns.client'
import {
  AgxButton,
  AgxColumn,
  AgxModal,
  AgxRow,
  AgxSegmentPicker,
  AgxTypography,
} from '@urbanx/agx.ui-components2'
import { Images } from '@urbanx/agx-ui-components'
import { useCallback, useEffect, useState } from 'react'
import useDefaultDepositPayableValue from '../components/useDefaultDepositPayableValue'
import { FormConditionOnDate } from '../components/FormConditionOnDate'
import FormConditionNDays from '../components/FormConditionNDays'

interface NewFormConditionModalProps {
  id: string
  modalTitle: string
  formCondition?: FormCondition
  availableConditions?: FormConditionCondition[] // Deprecated - use onDateConditions and fromDateConditions instead
  onDateConditions?: FormConditionCondition[]
  fromDateConditions?: FormConditionCondition[]
  errorMessage?: string
  setFormCondition: (_condition: FormCondition | undefined) => void
  onConfirmClick: () => void
  openModal: (_isOpen: boolean) => void
  validate?: boolean
}

const SegmentOptions = [
  { title: 'on...', value: DepositPayable.On },
  { title: '# days from...', value: DepositPayable.NDaysFrom },
]

export function NewFormConditionModal({
  id,
  modalTitle,
  formCondition,
  availableConditions,
  onDateConditions,
  fromDateConditions,
  errorMessage,
  setFormCondition,
  onConfirmClick,
  openModal,
  validate,
}: NewFormConditionModalProps) {
  const [value, setValue] = useState<FormCondition>(
    formCondition as FormCondition
  )

  const defaultSegment = useDefaultDepositPayableValue(formCondition)

  const [selectedSegment, setSelectedSegment] = useState<string>(defaultSegment)

  // For backward compatibility, use availableConditions if new props not provided
  const onDateOptions = onDateConditions || availableConditions || []
  const fromDateOptions = fromDateConditions || availableConditions || []

  const onSelectedSegmentChange = useCallback(
    (segment: string) => {
      setSelectedSegment(segment)
      setValue((prev) => ({
        ...prev,
        depositPayable: segment as DepositPayable,
      }))
    },
    [setSelectedSegment, setValue]
  )

  useEffect(() => {
    setFormCondition(value)
  }, [value, setFormCondition])

  return (
    <AgxModal
      title={modalTitle}
      variant="withDivider"
      type="single"
      actionButtonContainerStyle="withDivider"
      setIsDialogOpen={openModal}
      minHeight="190px"
      content={
        <AgxColumn
          gap="spacing-lg"
          padding={{ top: 'md', bottom: 'md', left: 'xl', right: 'xl' }}
        >
          <AgxColumn id={id} gap="spacing-md" fill>
            <AgxColumn gap="spacing-xl" fill>
              <AgxSegmentPicker
                id={`${id}-depositPayableSegment`}
                segments={SegmentOptions}
                onChange={onSelectedSegmentChange}
                defaultValue={selectedSegment}
              />
              {selectedSegment === DepositPayable.On && (
                <FormConditionOnDate
                  id={id}
                  onSpecificDateOptions={onDateOptions}
                  defaultValue={value}
                  onValueChange={(value) => setValue(value as FormCondition)}
                />
              )}
              {selectedSegment === DepositPayable.NDaysFrom && (
                <FormConditionNDays
                  id={id}
                  fromDateOptions={fromDateOptions}
                  defaultValue={value}
                  hasPriceOrPercentage={false}
                  onValueChange={(value) => setValue(value as FormCondition)}
                  validate={validate}
                />
              )}
            </AgxColumn>
          </AgxColumn>
          {validate && errorMessage && (
            <div className={'errorMessage'}>
              <Images.AlertCircle type="Danger" />
              <AgxTypography id={`${id}-error`} type="form_error" size="small">
                {errorMessage}
              </AgxTypography>
            </div>
          )}
        </AgxColumn>
      }
      actionButtons={
        <AgxRow gap="spacing-md" alignItems="flex-end" fill>
          <AgxButton
            id="cancel-add-finance"
            variant="hollow"
            text="Cancel"
            size="large"
            stretch
            onClick={() => openModal(false)}
          />
          <AgxButton
            id="save-add-finance"
            variant="primary"
            text="Save"
            size="large"
            stretch
            onClick={onConfirmClick}
            disabled={validate && !!errorMessage}
          />
        </AgxRow>
      }
    />
  )
}
